import React, { lazy } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import useDirection from "./utils/changeDirection";

// Layouts
import LanguageLayout from "./components/global/LanguageLayout";
import PublicShell from "./components/layouts/PublicShell";
import RouteTracker from "./components/analytics/RouteTracker";

// Admin
import AdminLogin from "./components/admin/Lgoin";
import CrmLogin from "./components/admin/CrmLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminReferrals from "./components/admin/AdminReferrals";
import AdminServices from "./components/admin/AdminServices";
import AdminContacts from "./components/admin/AdminContacts";
import Dashboard from "./components/admin/Dashboard";

// CRM
import LeadList from "./components/admin/crm/leads/LeadList";
import LeadDetail from "./components/admin/crm/leads/LeadDetail";
import LeadForm from "./components/admin/crm/leads/LeadForm";
import ReferralSourceList from "./components/admin/crm/referral-sources/ReferralSourceList";
import ReferralSourceDetail from "./components/admin/crm/referral-sources/ReferralSourceDetail";
import ReferralSourceForm from "./components/admin/crm/referral-sources/ReferralSourceForm";
import TaskList from "./components/admin/crm/tasks/TaskList";
import TaskDetail from "./components/admin/crm/tasks/TaskDetail";
import TaskForm from "./components/admin/crm/tasks/TaskForm";
import UserList from "./components/admin/crm/users/UserList";
import UserForm from "./components/admin/crm/users/UserForm";
import Reports from "./components/admin/crm/reports/Reports";

// Public Pages - Lazy loaded
const Home = lazy(() => import("./components/pages/Home"));
const Services = lazy(() => import("./components/pages/Services"));
const ServiceDetail = lazy(() => import("./components/pages/ServiceDetail"));
const Insurances = lazy(() => import("./components/pages/Insurances"));
const AboutUs = lazy(() => import("./components/pages/AboutUs"));
const ContactUs = lazy(() => import("./components/pages/ContactUs"));
const Faq = lazy(() => import("./components/pages/Faq"));
const HelpfulArticles = lazy(() => import("./components/pages/HelpfulArticles"));
const ArticleDetail = lazy(() => import("./components/pages/ArticleDetail"));
const PrivacyPolicy = lazy(() => import("./components/pages/PrivacyPolicy"));
const TermAndConditions = lazy(() => import("./components/pages/TermAndConditions"));
const MedicareHospice = lazy(() => import("./components/pages/MedicareHospice"));
const Referrals = lazy(() => import("./components/pages/Referrals"));
const RoutineHomeCare = lazy(() => import("./components/pages/RoutineHomeCare"));
const ContinuousHomeCare = lazy(() => import("./components/pages/ContinuousHomeCare"));
const InpatientRespiteCare = lazy(() => import("./components/pages/InpatientRespiteCare"));
const GeneralInpatientCare = lazy(() => import("./components/pages/GeneralInpatientCare"));
const Resources = lazy(() => import("./components/pages/Resources"));
const LocationsHub = lazy(() => import("./components/pages/LocationsHub"));
const LocationsIndex = lazy(() => import("./components/pages/LocationsIndex"));
const CityLandingPage = lazy(() => import("./components/pages/CityLandingPage"));
const LeadCapture = lazy(() => import("./components/pages/LeadCapture"));

const App = () => {
  useDirection();

  return (
    <>
      <RouteTracker />
      <Routes>
        {/* --- Admin Routes (No Language Prefix) --- */}
        <Route path="/admin/auth/login" element={<AdminLogin />} />
        <Route path="/admin/crm/login" element={<CrmLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="referrals" element={<AdminReferrals />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="contacts" element={<AdminContacts />} />

          {/* CRM Routes */}
          <Route path="leads" element={<LeadList />} />
          <Route path="leads/new" element={<LeadForm />} />
          <Route path="leads/:id" element={<LeadDetail />} />
          <Route path="leads/:id/edit" element={<LeadForm />} />

          <Route path="referral-sources" element={<ReferralSourceList />} />
          <Route path="referral-sources/new" element={<ReferralSourceForm />} />
          <Route path="referral-sources/:id" element={<ReferralSourceDetail />} />
          <Route path="referral-sources/:id/edit" element={<ReferralSourceForm />} />

          <Route path="tasks" element={<TaskList />} />
          <Route path="tasks/new" element={<TaskForm />} />
          <Route path="tasks/:id" element={<TaskDetail />} />
          <Route path="tasks/:id/edit" element={<TaskForm />} />

          <Route path="users" element={<UserList />} />
          <Route path="users/new" element={<UserForm />} />
          <Route path="users/:id/edit" element={<UserForm />} />
          <Route path="reports" element={<Reports />} />
        </Route>

        {/* --- Language Root & Public Routes --- */}
        <Route path="/:lang" element={<LanguageLayout />}>
          <Route element={<PublicShell />}>
            <Route index element={<Home />} />
            <Route path="services" element={<Services />} />
            <Route path="services/routine-home-care" element={<RoutineHomeCare />} />
            <Route path="services/continuous-home-care" element={<ContinuousHomeCare />} />
            <Route path="services/inpatient-respite-care" element={<InpatientRespiteCare />} />
            <Route path="services/general-inpatient-care" element={<GeneralInpatientCare />} />
            <Route path="services/:serviceId" element={<ServiceDetail />} />
            <Route path="insurances" element={<Insurances />} />
            <Route path="about" element={<AboutUs />} />
            <Route path="contact" element={<ContactUs />} />
            <Route path="faq" element={<Faq />} />
            <Route path="helpful-articles" element={<HelpfulArticles />} />
            <Route path="helpful-articles/:slug" element={<ArticleDetail />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="term-and-conditions" element={<TermAndConditions />} />
            <Route path="medicare-hospice" element={<MedicareHospice />} />
            <Route path="referrals" element={<Referrals />} />

            <Route path="resources" element={<Resources />} />
            <Route path="locations" element={<LocationsHub />} />
            <Route path="locations-guide" element={<LocationsIndex />} />
            <Route path="services/:county/:citySlug" element={<CityLandingPage />} />
            <Route path="lead-capture" element={<LeadCapture />} />
          </Route>
        </Route>

        <Route path="/" element={<Navigate to="/en" replace />} />
        <Route path="*" element={<Navigate to="/en" replace />} />
      </Routes>
    </>
  );
};

export default App;
